import { useMemo, useState } from "react";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { FIELD_NAME_TO_TRANSLATION_LABEL, LineChartProps } from "./LineChartCommon";
import { isTriPhaseDevice } from "../../../../lib/Device";

const PHASE_COLORS = {
    u_1: "#012124",
    u_2: "#3b6bb8",
    u_3: "#F7AA3F",
} as const;

const POWER_DATA_KEYS = new Set(["soliseco_power", "p_1_soliseco", "p_2_soliseco", "p_3_soliseco"]);

const DropoutLineChart = ({ lineData, currentDevice, ticks, lineChartXTickFormatter }: LineChartProps) => {
    const { t } = useTranslation("dashboard");
    const isTriPhase = isTriPhaseDevice(currentDevice);
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});

    const translateSeries = (key: string) =>
        t(FIELD_NAME_TO_TRANSLATION_LABEL[key] ?? key, {
            device_name: currentDevice?.name,
        });

    const isSeriesHidden = (key: string) => !!hiddenSeries[key];

    const toggleSeries = (key: string) => {
        setHiddenSeries((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const renderCustomizedTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const pt = lineData.find((elem: any) => elem.time === label);
            if (pt) {
                return (
                <div className="tooltip-container">
                    <p style={{ fontWeight: 700 }}>{lineChartXTickFormatter(parseInt(pt.time))}</p>
                    {payload.map((line: any, index: number) => (
                    <p key={index} style={{ color: line.stroke, fontWeight: 500 }}>
                        {translateSeries(line.name ?? line.dataKey)}:{" "}
                            { pt[line.dataKey] } {POWER_DATA_KEYS.has(line.dataKey) ? "W" : "V" }
                    </p>
                    ))}
                </div>
                );
            }
        }
        return <></>;
    };

    const [pDataMin, pDataMax] = useMemo(() => {
        const powerKeys = isTriPhase
            ? ["soliseco_power", "p_1_soliseco", "p_2_soliseco", "p_3_soliseco"]
            : ["soliseco_power"];
        const values = lineData.flatMap((pt: any) =>
            powerKeys.map((key) => Number(pt[key] ?? 0))
        );
        const globalMin = Math.min(...values);
        const globalMax = Math.max(...values);
        const min: number = Math.round((globalMin * 1.1))
        const roundedMin = min - (10 + (min % 10))
        const max: number = Math.round((globalMax * 1.1))
        const roundedMax =  max + (10 - (max % 10))
        return [Math.min(-50, parseInt(roundedMin.toFixed(0))), Math.max(50, parseInt(roundedMax.toFixed(0)))]
    }, [lineData, isTriPhase])

    const compareNumbers = (a: number, b: number) => {
        return a - b;
    }
    
    const pYTicks = useMemo(() => {
        const interval = Math.round((Math.abs(pDataMin) + Math.abs(pDataMax)) / 3)
        const roundedInterval = interval + (10 - (interval % 10))
        const negativeTickCnt: number = Math.floor(Math.abs(pDataMin) / roundedInterval)
        const positiveTickCnt: number = Math.floor(Math.abs(pDataMax) / roundedInterval)
        const negativeTicks = Array.from({ length: negativeTickCnt }, (_, i) => { return -(i + 1) * roundedInterval })
        const positiveTicks = Array.from({ length: positiveTickCnt }, (_, i) => { return (i + 1) * roundedInterval })
        const ticks = [pDataMin, ...negativeTicks, 0, ...positiveTicks, pDataMax]
        const uniqueTicks = ticks.filter((value, index, array) => ticks.indexOf(value) === index);
        uniqueTicks.sort(compareNumbers)
        return uniqueTicks
    }, [pDataMin, pDataMax])

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                width={500}
                height={300}
                data={lineData}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey='time'
                    name='Time'
                    scale='time'
                    type='number'
                    domain={['dataMin', 'dataMax']} 
                    tickFormatter={ lineChartXTickFormatter }
                    ticks={ticks}
                />
                <YAxis
                    yAxisId="voltage"
                    unit="V"
                    ticks={[220, 230, 240, 250, 260]}
                    domain={ [220, 260] }
                    allowDataOverflow
                />
                <YAxis
                    yAxisId="power"
                    orientation="right"
                    unit="W"
                    domain={ () => [pDataMin, pDataMax] }
                    ticks={ pYTicks }
                />
                <Tooltip content={renderCustomizedTooltip}/>
                <Legend
                    formatter={(value) => (
                        <span
                            style={{
                                color: "inherit",
                                opacity: isSeriesHidden(value) ? 0.35 : 1,
                                cursor: "pointer",
                            }}
                        >
                            {translateSeries(value)}
                        </span>
                    )}
                    onClick={(entry) => {
                        const key = String(entry.value ?? entry.dataKey ?? "");
                        if (key) toggleSeries(key);
                    }}
                />
                {isTriPhase ? (
                    <>
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="u_1"
                            name="u_1"
                            hide={isSeriesHidden("u_1")}
                            stroke={PHASE_COLORS.u_1}
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="u_2"
                            name="u_2"
                            hide={isSeriesHidden("u_2")}
                            stroke={PHASE_COLORS.u_2}
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="u_3"
                            name="u_3"
                            hide={isSeriesHidden("u_3")}
                            stroke={PHASE_COLORS.u_3}
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="dropout_v_max"
                            name="dropout_v_max_l1"
                            hide={isSeriesHidden("dropout_v_max_l1")}
                            stroke={PHASE_COLORS.u_1}
                            strokeDasharray="6 4"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="dropout_v_max_l2"
                            name="dropout_v_max_l2"
                            hide={isSeriesHidden("dropout_v_max_l2")}
                            stroke={PHASE_COLORS.u_2}
                            strokeDasharray="6 4"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="dropout_v_max_l3"
                            name="dropout_v_max_l3"
                            hide={isSeriesHidden("dropout_v_max_l3")}
                            stroke={PHASE_COLORS.u_3}
                            strokeDasharray="6 4"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="power"
                            dataKey="p_1_soliseco"
                            name="p_1_soliseco"
                            hide={isSeriesHidden("p_1_soliseco")}
                            stroke={PHASE_COLORS.u_1}
                            strokeDasharray="2 2"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="power"
                            dataKey="p_2_soliseco"
                            name="p_2_soliseco"
                            hide={isSeriesHidden("p_2_soliseco")}
                            stroke={PHASE_COLORS.u_2}
                            strokeDasharray="2 2"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            yAxisId="power"
                            dataKey="p_3_soliseco"
                            name="p_3_soliseco"
                            hide={isSeriesHidden("p_3_soliseco")}
                            stroke={PHASE_COLORS.u_3}
                            strokeDasharray="2 2"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                    </>
                ) : (
                    <>
                        {currentDevice?.installation_phase === 1 && (
                            <Line
                                type="monotone"
                                yAxisId="voltage"
                                dataKey="u_1"
                                name="u_1"
                                hide={isSeriesHidden("u_1")}
                                stroke={PHASE_COLORS.u_2}
                                activeDot={{ r: 4 }}
                                dot={false}
                            />
                        )}
                        {currentDevice?.installation_phase === 2 && (
                            <Line
                                type="monotone"
                                yAxisId="voltage"
                                dataKey="u_2"
                                name="u_2"
                                hide={isSeriesHidden("u_2")}
                                stroke={PHASE_COLORS.u_2}
                                activeDot={{ r: 4 }}
                                dot={false}
                            />
                        )}
                        {currentDevice?.installation_phase === 3 && (
                            <Line
                                type="monotone"
                                yAxisId="voltage"
                                dataKey="u_3"
                                name="u_3"
                                hide={isSeriesHidden("u_3")}
                                stroke={PHASE_COLORS.u_2}
                                activeDot={{ r: 4 }}
                                dot={false}
                            />
                        )}
                        <Line
                            type="monotone"
                            yAxisId="voltage"
                            dataKey="dropout_v_max"
                            name="dropout_v_max"
                            hide={isSeriesHidden("dropout_v_max")}
                            stroke={PHASE_COLORS.u_1}
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                    </>
                )}
                <Line
                    type="monotone"
                    yAxisId="power"
                    dataKey="soliseco_power"
                    name="soliseco_power"
                    hide={isSeriesHidden("soliseco_power")}
                    stroke="#82BA71"
                    activeDot={{ r: 4 }}
                    dot={false}
                />
            </LineChart>
        </ResponsiveContainer>
    )
}

export default DropoutLineChart
